import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Stethoscope, 
  Sparkles, 
  Wand2, 
  Zap, 
  Heart, 
  Clock, 
  ArrowRight,
  Copy,
  Check,
  RefreshCw,
  TrendingUp,
  MessageSquare,
  Target,
  Loader2,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  AlertCircle,
  Video,
  Play
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import { scriptDoctorAPI } from "../lib/api";

export default function ScriptDoctorPage() {
  const navigate = useNavigate();
  const [script, setScript] = useState("");
  const [platform, setPlatform] = useState("tiktok");
  const [niche, setNiche] = useState("general");
  const [tone, setTone] = useState("energetic");
  const [duration, setDuration] = useState(30);
  const [loading, setLoading] = useState(false);
  const [quickPolishLoading, setQuickPolishLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [copiedItem, setCopiedItem] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    hooks: true,
    ctas: true,
    rewrite: true,
    quickFixes: false,
    emotional: false,
    pacing: false
  });

  const handleAnalyze = async () => {
    if (!script.trim()) {
      toast.error("Please enter a script to analyze");
      return;
    }

    setLoading(true);
    try {
      const response = await scriptDoctorAPI.analyze(script, platform, niche, tone, duration);
      setResults(response.data);
      toast.success("Script analyzed! Check out your improvements below.");
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze script");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPolish = async () => {
    if (!script.trim()) {
      toast.error("Please enter a script to polish");
      return;
    }

    setQuickPolishLoading(true);
    try {
      const response = await scriptDoctorAPI.quickPolish(script);
      if (response.data.polished_script) {
        // Ensure polished_script is a string
        const polishedScript = typeof response.data.polished_script === 'string' 
          ? response.data.polished_script 
          : (response.data.polished_script?.option_1 || String(response.data.polished_script));
        setScript(polishedScript);
        toast.success(`Script polished! Improvement score: ${response.data.improvement_score}/10`);
      }
    } catch (error) {
      console.error("Quick polish error:", error);
      toast.error("Failed to polish script");
    } finally {
      setQuickPolishLoading(false);
    }
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedItem(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const useRewrittenScript = () => {
    if (results?.rewritten_script?.full_script) {
      setScript(results.rewritten_script.full_script);
      toast.success("Rewritten script applied!");
    }
  };

  // Navigate to Create Video with the improved script and suggestions
  const handleCreateVideo = () => {
    // Use the rewritten script if available, otherwise use the current script
    const finalScript = results?.rewritten_script?.full_script || script;
    
    if (!finalScript.trim()) {
      toast.error("Please have a script ready before creating a video");
      return;
    }

    // Gather all the improved content
    const videoData = {
      fromScriptDoctor: true,
      script: finalScript,
      // Generate a title from the first hook or script content
      // Hooks can be objects with {text, style} or strings
      suggestedTitle: (typeof results?.improved_hooks?.[0] === 'string' 
        ? results?.improved_hooks?.[0] 
        : results?.improved_hooks?.[0]?.text || ''
      ).replace(/["""]/g, '').slice(0, 60) || 
                      finalScript.split('.')[0].slice(0, 60) || 
                      "My Viral Video",
      // Include hooks for title options - extract text if objects
      hooks: (results?.improved_hooks || []).map(h => typeof h === 'string' ? h : h?.text || ''),
      // Include CTAs - extract text if objects
      ctas: (results?.improved_ctas || []).map(c => typeof c === 'string' ? c : c?.text || ''),
      // Include power words for reference
      powerWords: results?.power_words || [],
      // Include analysis data
      viralScore: results?.analysis?.viral_score,
      // Platform preference
      platform: platform,
      // Suggested duration based on analysis
      suggestedDuration: duration,
      // Tone for voice selection
      tone: tone
    };

    toast.success("Navigating to Create Video with your improved script!");
    navigate("/create", { state: { scriptDoctor: videoData } });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-400";
    if (score >= 60) return "text-yellow-400";
    if (score >= 40) return "text-orange-400";
    return "text-red-400";
  };

  const getRatingColor = (rating) => {
    if (rating >= 8) return "bg-green-500";
    if (rating >= 6) return "bg-yellow-500";
    if (rating >= 4) return "bg-orange-500";
    return "bg-red-500";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 max-w-6xl mx-auto"
    >
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
          <Stethoscope className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white">AI Script Doctor</h1>
          <p className="text-muted-foreground">Transform your scripts into viral content</p>
        </div>
        <Badge className="ml-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
          Powered by AI
        </Badge>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="space-y-4">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-cyan-400" />
                Your Script
              </CardTitle>
              <CardDescription>Paste your script or write what you want and let me do the rest</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Paste your script here... 

Example: Hey guys, I just found this amazing product and I had to share it with you. It's so good and I think you'll love it too. Check out the link in my bio!"
                value={script}
                onChange={(e) => setScript(e.target.value)}
                className="min-h-[200px] bg-secondary/30 border-border/50 resize-none"
                data-testid="script-input"
              />
              
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{(typeof script === 'string' ? script : '').split(/\s+/).filter(w => w).length} words</span>
                <span>~{Math.ceil((typeof script === 'string' ? script : '').split(/\s+/).filter(w => w).length / 2.5)}s reading time</span>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleQuickPolish}
                  disabled={quickPolishLoading || !script.trim()}
                  className="flex-1"
                >
                  {quickPolishLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Wand2 className="w-4 h-4 mr-2" />
                  )}
                  Quick Polish / Generate Script
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setScript("")}
                  disabled={!script}
                >
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Settings */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Target className="w-4 h-4 text-purple-400" />
                Analysis Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Platform</Label>
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger className="bg-secondary/30 border-border/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tiktok">TikTok</SelectItem>
                      <SelectItem value="instagram">Instagram Reels</SelectItem>
                      <SelectItem value="youtube">YouTube Shorts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Niche</Label>
                  <Select value={niche} onValueChange={setNiche}>
                    <SelectTrigger className="bg-secondary/30 border-border/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="beauty">Beauty</SelectItem>
                      <SelectItem value="fitness">Fitness</SelectItem>
                      <SelectItem value="tech">Tech</SelectItem>
                      <SelectItem value="lifestyle">Lifestyle</SelectItem>
                      <SelectItem value="food">Food</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="education">Education</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Tone</Label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger className="bg-secondary/30 border-border/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="energetic">Energetic</SelectItem>
                      <SelectItem value="calm">Calm & Confident</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="funny">Funny</SelectItem>
                      <SelectItem value="dramatic">Dramatic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Duration: {duration}s</Label>
                  <Slider
                    value={[duration]}
                    onValueChange={([v]) => setDuration(v)}
                    min={15}
                    max={60}
                    step={5}
                    className="mt-2"
                  />
                </div>
              </div>

              <Button
                onClick={handleAnalyze}
                disabled={loading || !script.trim()}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                data-testid="analyze-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze & Improve Script
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Results Section */}
        <div className="space-y-4">
          {!results ? (
            <Card className="bg-card border-border/50 h-full flex items-center justify-center min-h-[400px]">
              <CardContent className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mx-auto mb-4">
                  <Lightbulb className="w-10 h-10 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Ready to Transform Your Script</h3>
                <p className="text-muted-foreground max-w-sm mx-auto">
                  Paste your script on the left and click "Analyze" to get AI-powered improvements, 
                  viral hooks, better CTAs, and more!
                </p>
              </CardContent>
            </Card>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Score Card */}
                {results.analysis && (
                  <Card className="bg-card border-border/50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-cyan-400" />
                          Script Analysis
                        </span>
                        <span className={`text-3xl font-bold ${getScoreColor(results.analysis.viral_score)}`}>
                          {results.analysis.viral_score}/100
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-4 gap-3">
                        {[
                          { label: "Hook", value: results.analysis.hook_rating },
                          { label: "CTA", value: results.analysis.cta_rating },
                          { label: "Pacing", value: results.analysis.pacing_rating },
                          { label: "Emotion", value: results.analysis.emotional_impact },
                        ].map((item) => (
                          <div key={item.label} className="text-center">
                            <div className="text-xs text-muted-foreground mb-1">{item.label}</div>
                            <div className="relative h-2 bg-secondary rounded-full overflow-hidden">
                              <div 
                                className={`absolute inset-y-0 left-0 ${getRatingColor(item.value)}`}
                                style={{ width: `${item.value * 10}%` }}
                              />
                            </div>
                            <div className="text-sm font-medium text-white mt-1">{item.value}/10</div>
                          </div>
                        ))}
                      </div>

                      {results.analysis.strengths && (
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-green-400">Strengths:</div>
                          <div className="flex flex-wrap gap-2">
                            {results.analysis.strengths.map((s, i) => (
                              <Badge key={i} variant="outline" className="border-green-500/50 text-green-400">
                                {s}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {results.analysis.weaknesses && (
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-orange-400">Areas to Improve:</div>
                          <div className="flex flex-wrap gap-2">
                            {results.analysis.weaknesses.map((w, i) => (
                              <Badge key={i} variant="outline" className="border-orange-500/50 text-orange-400">
                                {w}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Improved Hooks */}
                {results.improved_hooks && (
                  <Card className="bg-card border-border/50">
                    <CardHeader 
                      className="pb-3 cursor-pointer"
                      onClick={() => toggleSection("hooks")}
                    >
                      <CardTitle className="text-white flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Zap className="w-5 h-5 text-yellow-400" />
                          Viral Hooks ({results.improved_hooks.length})
                        </span>
                        {expandedSections.hooks ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </CardTitle>
                    </CardHeader>
                    <AnimatePresence>
                      {expandedSections.hooks && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          <CardContent className="space-y-3">
                            {results.improved_hooks.map((hook, index) => (
                              <div 
                                key={index}
                                className="p-3 rounded-lg bg-secondary/30 border border-border/30 group"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <Badge variant="outline" className="mb-2 text-xs capitalize">
                                      {hook.style}
                                    </Badge>
                                    <p className="text-white">{hook.text}</p>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => copyToClipboard(hook.text, `hook-${index}`)}
                                  >
                                    {copiedItem === `hook-${index}` ? (
                                      <Check className="w-4 h-4 text-green-400" />
                                    ) : (
                                      <Copy className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </CardContent>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                )}

                {/* Improved CTAs */}
                {results.improved_ctas && (
                  <Card className="bg-card border-border/50">
                    <CardHeader 
                      className="pb-3 cursor-pointer"
                      onClick={() => toggleSection("ctas")}
                    >
                      <CardTitle className="text-white flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <ArrowRight className="w-5 h-5 text-green-400" />
                          Better CTAs ({results.improved_ctas.length})
                        </span>
                        {expandedSections.ctas ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </CardTitle>
                    </CardHeader>
                    <AnimatePresence>
                      {expandedSections.ctas && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          <CardContent className="space-y-3">
                            {results.improved_ctas.map((cta, index) => (
                              <div 
                                key={index}
                                className="p-3 rounded-lg bg-secondary/30 border border-border/30 group"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <Badge variant="outline" className="mb-2 text-xs capitalize">
                                      {cta.style}
                                    </Badge>
                                    <p className="text-white">{cta.text}</p>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => copyToClipboard(cta.text, `cta-${index}`)}
                                  >
                                    {copiedItem === `cta-${index}` ? (
                                      <Check className="w-4 h-4 text-green-400" />
                                    ) : (
                                      <Copy className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </CardContent>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                )}

                {/* Rewritten Script */}
                {results.rewritten_script && (
                  <Card className="bg-card border-border/50">
                    <CardHeader 
                      className="pb-3 cursor-pointer"
                      onClick={() => toggleSection("rewrite")}
                    >
                      <CardTitle className="text-white flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-purple-400" />
                          Rewritten Script
                        </span>
                        {expandedSections.rewrite ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </CardTitle>
                    </CardHeader>
                    <AnimatePresence>
                      {expandedSections.rewrite && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          <CardContent className="space-y-3">
                            <div className="p-4 rounded-lg bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30">
                              <p className="text-white whitespace-pre-wrap">{results.rewritten_script.full_script}</p>
                              <div className="flex items-center justify-between mt-4 pt-3 border-t border-purple-500/20">
                                <div className="flex gap-4 text-sm text-muted-foreground">
                                  <span>{results.rewritten_script.word_count} words</span>
                                  <span>{results.rewritten_script.estimated_duration}</span>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => copyToClipboard(results.rewritten_script.full_script, "rewrite")}
                                  >
                                    {copiedItem === "rewrite" ? (
                                      <Check className="w-4 h-4 mr-1 text-green-400" />
                                    ) : (
                                      <Copy className="w-4 h-4 mr-1" />
                                    )}
                                    Copy
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={useRewrittenScript}
                                    className="bg-purple-500 hover:bg-purple-600"
                                  >
                                    Use This Script
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                )}

                {/* Power Words & Emotional Triggers */}
                {(results.power_words || results.emotional_triggers) && (
                  <Card className="bg-card border-border/50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white flex items-center gap-2">
                        <Heart className="w-5 h-5 text-red-400" />
                        Power Elements
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {results.power_words && (
                        <div>
                          <Label className="text-muted-foreground text-sm mb-2 block">Power Words to Use:</Label>
                          <div className="flex flex-wrap gap-2">
                            {results.power_words.map((word, i) => (
                              <Badge key={i} className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                                {word}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {results.emotional_triggers && (
                        <div>
                          <Label className="text-muted-foreground text-sm mb-2 block">Emotional Triggers:</Label>
                          <div className="flex flex-wrap gap-2">
                            {results.emotional_triggers.map((trigger, i) => (
                              <Badge key={i} className="bg-pink-500/20 text-pink-400 border-pink-500/30">
                                {trigger}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Quick Fixes */}
                {results.quick_fixes && results.quick_fixes.length > 0 && (
                  <Card className="bg-card border-border/50">
                    <CardHeader 
                      className="pb-3 cursor-pointer"
                      onClick={() => toggleSection("quickFixes")}
                    >
                      <CardTitle className="text-white flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-orange-400" />
                          Quick Fixes ({results.quick_fixes.length})
                        </span>
                        {expandedSections.quickFixes ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </CardTitle>
                    </CardHeader>
                    <AnimatePresence>
                      {expandedSections.quickFixes && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          <CardContent className="space-y-3">
                            {results.quick_fixes.map((fix, index) => (
                              <div 
                                key={index}
                                className="p-3 rounded-lg bg-secondary/30 border border-border/30"
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-red-400 line-through text-sm">{fix.original}</span>
                                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                                  <span className="text-green-400 text-sm">{fix.improved}</span>
                                </div>
                                <p className="text-xs text-muted-foreground">{fix.reason}</p>
                              </div>
                            ))}
                          </CardContent>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                )}

                {/* Create Video CTA - Show when we have results */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Card className="bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-orange-500/20 border-purple-500/30">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="text-center md:text-left">
                          <h3 className="text-xl font-bold text-white flex items-center gap-2 justify-center md:justify-start">
                            <Video className="w-6 h-6 text-purple-400" />
                            Ready to Create Your Video?
                          </h3>
                          <p className="text-muted-foreground mt-1">
                            Use your improved script, hooks, and CTAs to create a viral video
                          </p>
                        </div>
                        <Button
                          size="lg"
                          onClick={handleCreateVideo}
                          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-6 text-lg font-semibold shadow-lg shadow-purple-500/25"
                          data-testid="create-video-now-btn"
                        >
                          <Play className="w-5 h-5 mr-2" />
                          Create Video Now
                        </Button>
                      </div>
                      {results.analysis?.viral_score && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="flex flex-wrap gap-3 justify-center md:justify-start text-sm">
                            <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                              Viral Score: {results.analysis.viral_score}/100
                            </Badge>
                            {results.improved_hooks?.length > 0 && (
                              <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30">
                                {results.improved_hooks.length} Hooks Ready
                              </Badge>
                            )}
                            {results.improved_ctas?.length > 0 && (
                              <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                                {results.improved_ctas.length} CTAs Ready
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </motion.div>
  );
}
